// The Majlis guide (H-167 slice 2; docs/superpowers/plans/2026-10-19-guide-and-screen-help-plan.md): walkthroughs by
// outcome, each "before you start → steps → what you should see → when you're refused". Screenshots illustrate, the text
// stands without them; they come from the mock console only (fictional people, `e2e/guide_shots.py`), never production,
// because the bundle and its images are public. Every sentence about who may do what follows the API's own checks (the
// same as screens.content.ts); tests/guide.test.ts keeps the screens, routes and screenshots it names real.
import type { Who } from "@/lib/screens";
import shots from "./shots.json";

export interface GuideStep {
  id: string;
  title: string;
  text: string;            // what to do, and why
  to?: string;             // the screen in the console, as a path
  shot?: string;           // a screenshot's name in shots.json
}

export interface GuideRefusal {
  who: Who[];
  what: string;            // what they're refused, and what they see
  then: string;            // what to do instead
  shot?: string;
}

export interface GuideTopic {
  id: string;
  title: string;
  summary: string;
  forWhom: string;
  screens: string[];       // the screens.content keys it walks through ("About this screen" links back here)
  before: string[];
  steps: GuideStep[];
  see: string[];
  refused: GuideRefusal[];
  asOf: string;            // when it was last checked against the product
}

export interface GuideShot { name: string; path: string; role: string; alt: string; steps?: string[][]; wait?: number }
export const SHOTS: GuideShot[] = shots.shots;
export const SHOT_WIDTH = shots.width;
export const SHOT_HEIGHT = shots.height;

/** A screenshot's file, for the reader's theme. */
export function shotSrc(name: string, theme: "dark" | "light"): string {
  return `/guide/${name}.${theme}.${SHOT_WIDTH}.webp`;
}

export const TOPICS: GuideTopic[] = [
  {
    id: "ask-majlis",
    title: "Ask Majlis a question",
    summary: "Ask about a ticket, a production symptom, code or earlier runs. Majlis picks the agent that answers, or you name one, and you can follow every step it took.",
    forWhom: "Anyone in a workspace. Someone in no workspace reads, and asks once a workspace's admin adds them.",
    screens: ["ask"],
    before: [
      "You're in a workspace: a workspace's admin added you. Every question runs in one, with its agents, its connections and its budget.",
      "If the question is about a Jira ticket or a project, have its key at hand (for example OPS-412).",
    ],
    steps: [
      { id: "open", title: "Open Ask Majlis", to: "/ask", shot: "ask-start",
        text: "Choose Ask Majlis in the sidebar, or press ⌘K (Ctrl+K) and type “Ask”. Your earlier conversations are under Conversations; only you see them, and no admin reads them." },
      { id: "who", title: "Choose who answers, and where you ask from",
        text: "Leave “Majlis (picks the right agent)” to let Majlis route the question by each agent's purpose, or pick an agent by its @handle. Choose the workspace you ask from: the agents you can ask are your workspace's, the organisation's and those other workspaces share with yours. Add projects or Jira keys when the question is about them." },
      { id: "ask", title: "Write the question and send it", shot: "ask-answer",
        text: "Write it as you'd ask a colleague and press Ask (Enter sends; Shift+Enter starts a new line). You can attach files. The answer arrives with the run beside it: each step it took, which tools it called through the hub, its model and what it cost so far." },
      { id: "read", title: "Read the answer and its evidence",
        text: "The answer is headed by the agent's @handle. When Majlis picked the agent, the line under your question also says why (“Matched its purpose”) and offers the others: choose “Ask @…” to hear from one. An investigation lists what is likely, with its evidence, what was ruled out and what couldn't be checked." },
      { id: "improve", title: "Rate it, or suggest a correction",
        text: "Use 👍 or 👎, and “Suggest a correction” when the agent got something wrong about how your team works. Corrections go to the agent's owners, who may turn them into a lesson; nothing changes until they approve it." },
    ],
    see: [
      "The answer headed by the agent that gave it; when Majlis picked it, why it was chosen.",
      "The run's steps: admitted with a budget held, the model's calls and each tool call, all through the hub.",
      "The cost so far, and the conversation in your list under Conversations.",
    ],
    refused: [
      { who: ["viewer", "noWorkspace"], what: "In no workspace, the Workspace box says you aren't in one yet, and a question is refused: every question runs in a workspace.",
        then: "Ask an admin of your team's workspace to add you (Workspaces lists them). Until then you read tickets, runs and agents.", shot: "ask-viewer" },
      { who: ["member", "workspaceAdmin"], what: "Naming an agent your workspace may not use is refused before it runs.",
        then: "Leave Majlis to pick, or ask the agent's owners to share it with your workspace." },
    ],
    asOf: "2026-10-20",
  },
  {
    id: "create-an-agent",
    title: "Create an agent, get it reviewed, activate it",
    summary: "Build an agent for your team in the composer, save it as a draft, have a second person review what it may do, and activate it.",
    forWhom: "Organisation admins, and a workspace's admins for their workspace. Members read every agent and try those their workspace may use.",
    screens: ["agents", "agent"],
    before: [
      "You're an organisation admin, or an admin of the workspace the agent will belong to.",
      "Know what it's for (its objective), what starts it (Ask, a Jira board, a schedule, an alarm, another agent) and which connections it needs: an agent reaches a connection's tools only through the hub, and only the tools its definition lists.",
    ],
    steps: [
      { id: "new", title: "Start a new agent", to: "/agents", shot: "agents-list",
        text: "Open Agents and choose New agent. Agents are listed by @handle, grouped by the workspace they belong to; an organisation admin's can belong to the whole organisation." },
      { id: "compose", title: "Describe it in the composer", to: "/agents/new", shot: "agent-new",
        text: "Start from a template (Answer questions, Investigate tickets, Scheduled digest) or Blank. Give it a name (its @handle), a title and its objective: Majlis routes questions by the objective. Then add what starts it and what it uses: tools from connections, knowledge, skills, agents to hand off to. Each action is never, needs approval or autonomous; writes need a person's approval in Decisions. The Form and YAML show the same definition." },
      { id: "draft", title: "Save it as a draft",
        text: "Save as draft creates a new version that isn't active yet. To keep a new workspace agent private until its permissions are checked against the real hub, tick “Rehearse before publishing”." },
      { id: "review", title: "Have a second person review and activate it", to: "/agents/release-notes", shot: "agent-review",
        text: "On the agent's page, pick the version and read “Changes from” the active one. A version that lets the agent do more (an action level, a tool, a hand-off), or a workspace agent that can act, is activated by someone other than its author: another admin of the workspace, or an organisation admin. An organisation admin may activate their own drafts; the audit records it. Activate makes it the version new runs use." },
      { id: "use", title: "Put it to work",
        text: "Try it with “Try active v…”, which opens Ask with the agent named. Add it to your Jira boards so @handle works there, schedule it, or let a CloudWatch alarm wake it. Its owners can switch it off at any time in Availability." },
    ],
    see: [
      "The new version marked active on the agent's page, with who reviewed it.",
      "The agent in the Agents list under its workspace, with its active version and model.",
    ],
    refused: [
      { who: ["member", "viewer", "noWorkspace"], what: "There is no New agent button, and the API refuses a draft: only admins and a workspace's admins create agents.",
        then: "Ask one of your workspace's admins. You can read every agent and try those your workspace may use." },
      { who: ["workspaceAdmin"], what: "Activating your own version that adds a tool, an action or a hand-off is refused: “a second person reviews this before it's active”.",
        then: "Ask another admin of your workspace, or an organisation admin, to review the changes and activate it.", shot: "agent-author-refused" },
    ],
    asOf: "2026-10-20",
  },
  {
    id: "workspace-people",
    title: "Add people to your workspace and choose its admins",
    summary: "Add your team, several at once, invite those who haven't signed in yet, choose who administers the workspace, and take people out.",
    forWhom: "A workspace's admins, and organisation admins. Its members see who is in it.",
    screens: ["workspace"],
    before: [
      "You're an admin of the workspace: an organisation admin made you one, or another of its admins did.",
      "Have your people's work emails. Someone who hasn't signed in to Majlis yet can be invited.",
    ],
    steps: [
      { id: "open", title: "Open your workspace", to: "/workspaces", shot: "workspace-people",
        text: "Open Workspaces and choose yours. Its page shows the Jira projects it owns and those shared with it, the agents it uses, and its People." },
      { id: "add", title: "Add people, several at once",
        text: "In People, paste one or more work emails, separated by commas or new lines, and press Add; tick admin to make them admins. Someone who has signed in before joins at once; anyone else is invited and joins at their first sign-in. RushdBot tells them in Slack when it finds them there." },
      { id: "admins", title: "Choose its admins",
        text: "Tick or untick admin beside a person. A workspace's admins add and remove its people, create its agents, write its notes and skills, and connect its tools." },
      { id: "remove", title: "Take someone out, or withdraw an invitation",
        text: "Remove takes a person out of the workspace: their runs there stop at their next step. Withdraw cancels an invitation that hasn't been taken up." },
    ],
    see: [
      "Each person tagged Admin or Member; invitations tagged Invited, with who invited them.",
      "A line saying who joined, who was invited and what Slack did.",
    ],
    refused: [
      { who: ["member", "noWorkspace", "viewer"], what: "There is no Add form, no admin box and no Remove: a workspace's people are its admins' to change, and the API refuses anyone else.",
        then: "Ask one of the workspace's admins (the People list tags them)." },
      { who: ["workspaceAdmin"], what: "Another workspace's people aren't yours to change: the API refuses. Renaming a workspace is for organisation admins.",
        then: "Ask that workspace's admins, or an organisation admin." },
    ],
    asOf: "2026-10-20",
  },
  {
    id: "team-notes",
    title: "Write notes your agents read",
    summary: "Tell your agents what they should know about a Jira project: time zones, where things live, how your team reports. Every run on the project reads them.",
    forWhom: "The admins of the workspace that owns a project write its notes for every team; a workspace's admins write their workspace's own. Everyone who sees the project reads them.",
    screens: ["notes"],
    before: [
      "Pick the Jira project the note is about.",
      "Keep a note to facts an agent should apply every time; one note per subject, with a clear title.",
    ],
    steps: [
      { id: "open", title: "Open Notes and pick the project", to: "/notes", shot: "notes-new",
        text: "Open Notes, pick the project, and press New note. Only the notes you may write have Edit and Delete beside them." },
      { id: "write", title: "Write it, and choose whose it is",
        text: "Give it a title and write the note. Choose “every team” (the project's owning workspace's admins only) or your workspace's own: a workspace's own note reaches only that workspace's runs." },
      { id: "keep", title: "Keep it current",
        text: "Switch a note off to stop agents reading it without deleting it, edit it, or delete it." },
    ],
    see: [
      "Every run on the project gets its enabled notes with its instructions: the notes for every team always, a workspace's own only in that workspace's runs.",
    ],
    refused: [
      { who: ["member", "noWorkspace", "viewer"], what: "You read the notes, with no New note, Edit or Delete: the page says who writes them, and the API refuses a write.",
        then: "Ask an admin of your workspace to add or change a note.", shot: "notes-member" },
      { who: ["workspaceAdmin"], what: "A note for every team on a project your workspace doesn't own is refused: those are the owning workspace's admins'.",
        then: "Write your workspace's own note instead, or ask the owning workspace's admins." },
    ],
    asOf: "2026-10-20",
  },
  {
    id: "jira-majlis",
    title: "Start a fix from a Jira ticket with @majlis",
    summary: "Mention @majlis on a ticket: Majlis plans a fix as you, posts the plan on the ticket, and changes code only after a person approves it.",
    forWhom: "Anyone in a workspace that works on the ticket's project.",
    screens: ["ticket", "ticket:overview"],
    before: [
      "The project has @majlis on and a repository mapped to it (an organisation admin sets this up in Connect a project).",
      "You're in a workspace that works on the project: one that owns it, or one it is shared with.",
    ],
    steps: [
      { id: "mention", title: "Mention @majlis in a comment on the ticket",
        text: "Write a comment on the Jira ticket that says @majlis, with anything the agents should know. Majlis starts a fix run as you: it reads the ticket, the code and earlier attempts, and writes a plan. Nothing is changed yet." },
      { id: "plan", title: "Read the plan", to: "/tickets", shot: "ticket-plan",
        text: "Majlis posts the plan on the ticket with a link. On the ticket's page in the console, Overview shows “Plan to review”: the cause it found, the steps, the tests and the risks." },
      { id: "decide", title: "Approve it, or reject it with a reason",
        text: "Approve & implement makes the change in an isolated sandbox, has it reviewed, and opens a pull request for people to review and merge on GitHub. Reject ends the attempt; nothing is implemented. Deciding is for the people of a workspace that works on the project, the person who started it, and organisation admins." },
      { id: "iterate", title: "Ask for changes on the pull request",
        text: "On the pull request, write the change you want in a comment, then comment /majlis iterate: Majlis revises the pull request. Only the repository's owners, members and collaborators can ask; a bot's comment, or one over an hour old, starts nothing." },
    ],
    see: [
      "Majlis's comment on the ticket with the plan and its link; the attempt on the ticket's page, started by you, from Jira.",
      "After approval, a pull request by the Majlis GitHub App on its own branch, and its link on the ticket.",
    ],
    refused: [
      { who: ["noWorkspace", "viewer"], what: "Your mention is refused, and Majlis says why on the ticket: you aren't in a workspace that works on the project.",
        then: "Ask an admin of the project's workspace to add you; Workspaces shows who they are." },
      { who: ["member", "workspaceAdmin"], what: "In a workspace that doesn't work on the project, you read the plan but can't approve or reject it; the console says why.",
        then: "Ask the project's workspace to decide, or to share the project with yours." },
    ],
    asOf: "2026-10-20",
  },
  {
    id: "budgets",
    title: "Cap what agents spend",
    summary: "Set a monthly or daily limit for the organisation, a workspace, a project or one agent; a run that would pass it is refused before it starts.",
    forWhom: "Organisation admins.",
    screens: ["budgets"],
    before: [
      "Decide what to cap and over which period: the organisation, a workspace, a project or one agent; per month or per day.",
    ],
    steps: [
      { id: "open", title: "Open Budgets and add one", to: "/budgets", shot: "budgets",
        text: "Open Budgets: Spent this month shows the spend by workspace, project and agent, at list price. Press New budget." },
      { id: "set", title: "Choose what it covers, the period and the limit",
        text: "Pick what it covers (and which workspace, project or agent), per month or per day, the limit in dollars and the alert line. Save it; Edit changes it later." },
      { id: "watch", title: "Watch it",
        text: "Each budget's bar shows what is spent and what running work holds. Unknown usage holds, kept when a run's cost couldn't be measured, can be released with a reason." },
    ],
    see: [
      "Before a run starts, Majlis holds what it may cost against every budget that covers it. A run that would pass a limit is refused before it starts, and the person is told the budget is spent.",
      "Automated work (schedules, alarms) pauses at the alert line; a person's own question still runs until the limit.",
    ],
    refused: [
      { who: ["workspaceAdmin", "member", "noWorkspace", "viewer"], what: "Budgets is for organisation admins: the page sends you to the permission page and the API refuses.",
        then: "Ask an organisation admin to set or raise a budget." },
    ],
    asOf: "2026-10-20",
  },
  {
    id: "skills",
    title: "Write a skill and have it reviewed",
    summary: "Write the way your team does something (reading a ticket, investigating an incident) as a skill; it is used only once someone else reviews it.",
    forWhom: "Organisation admins, and a workspace's admins for their workspace's skills. Everyone reads them.",
    screens: ["skills"],
    before: [
      "A skill is reviewed instructions agents follow. An agent names the exact version it uses, so a new version changes nothing until an agent is moved to it.",
    ],
    steps: [
      { id: "write", title: "Write the draft", to: "/skills",
        text: "Open Skills and press New skill (or New version on an existing one): its name, a line on what it teaches, your workspace, and the skill in Markdown. Save the draft." },
      { id: "review", title: "Have someone else review it", shot: "skill-review",
        text: "Another admin of the workspace, or an organisation admin, reads the draft and its changes from the active version and presses Review and activate. Its author can't: “Your draft: someone else reviews it.”" },
      { id: "use", title: "Give it to an agent",
        text: "In an agent's definition, under Reviewed skills, pick the skill's version. A version that changes what an agent does is reviewed again when the agent's new version is activated." },
    ],
    see: [
      "The new version active, with who wrote it and who reviewed it; the previous one retired (agents pinned to it keep it).",
    ],
    refused: [
      { who: ["member", "noWorkspace", "viewer"], what: "You read every skill and its versions; there is no New skill or Review for you, and the API refuses both.",
        then: "Ask your workspace's admins." },
      { who: ["workspaceAdmin"], what: "Your own draft can't be reviewed by you, and another workspace's skills aren't yours to write or review.",
        then: "Ask another admin of your workspace, or an organisation admin, to review it." },
    ],
    asOf: "2026-10-20",
  },
  {
    id: "decisions",
    title: "Decide what an agent asks to do",
    summary: "When an agent wants to write through a connection, or hand work to another agent where its definition says a person approves, the request waits in Decisions until someone decides it, once.",
    forWhom: "The people of the workspace whose run asks, or whose connection it would write through. Organisation admins decide any.",
    screens: ["decisions"],
    before: [
      "An agent's definition says, for each action, never, needs approval or autonomous; only what needs approval waits here.",
      "A write through a connection is decided by the connection's own workspace, even when another workspace's run asked for it: a team's agents are the gates to its connections.",
    ],
    steps: [
      { id: "open", title: "Open Decisions", to: "/decisions", shot: "decisions",
        text: "Waiting lists what agents asked for: the action, its objective, the projects it's within, who asked and when it expires. View the exact request shows what would be done, word for word." },
      { id: "decide", title: "Approve or reject it",
        text: "Approving applies only to this exact version of the request and starts it at once; Majlis checks first that the person who asked may still do it. A write is made once, by Majlis's executor, never by the agent. Rejecting ends it." },
      { id: "history", title: "Look back",
        text: "History shows what was decided, by whom and what came of it; Levels shows each agent's actions and whether they wait for a person." },
    ],
    see: [
      "The request gone from Waiting and in History with your decision; for a write, its result once Majlis has made it.",
      "On the asker's conversation, the request, the decision and the result in its steps.",
    ],
    refused: [
      { who: ["member", "workspaceAdmin"], what: "A request for another workspace's connection, or a run of a workspace you're not in, isn't yours to decide: the API refuses it.",
        then: "That workspace's people decide it; an organisation admin can too." },
      { who: ["noWorkspace", "viewer"], what: "Nothing waits on you: decisions belong to workspaces. You can read each agent's levels.",
        then: "Ask a workspace's admin to add you." },
    ],
    asOf: "2026-10-20",
  },
  {
    id: "cloudwatch",
    title: "Connect your team's CloudWatch",
    summary: "Give your team's agents your logs, metrics, alarms and dashboards, read through a read-only role your engineers create in your own AWS account.",
    forWhom: "A workspace's admins, for their workspace (and organisation admins). Your engineer creates the role in your AWS account.",
    screens: ["connectors", "connector:new:cloudwatch"],
    before: [
      "Know the AWS account and region, and what your agents may read: log groups, metric namespaces (and dimensions), alarm and dashboard name prefixes.",
      "Someone who can create a CloudFormation stack in that account (your engineer) is at hand for one step.",
    ],
    steps: [
      { id: "new", title: "Add a connection: CloudWatch", to: "/connectors/new?kind=cloudwatch", shot: "cloudwatch-new",
        text: "Open Connectors, Add a connection, CloudWatch. Choose your workspace, a title and a name (how agents' definitions name it), the AWS account id and the region, and continue." },
      { id: "reads", title: "Say what it may read",
        text: "Add the log groups, metric namespaces (and dimensions), alarm and dashboard prefixes your agents may read, and save. Nothing outside them is ever read." },
      { id: "role", title: "Your engineer creates the role",
        text: "The page shows one command with this connection's own role name and external id. Your engineer runs it in your account: it creates a read-only role that only Majlis can assume, limited to what you listed." },
      { id: "test", title: "Test, confirm and activate",
        text: "Test as Majlis checks that Majlis can read what you listed and that the role refuses anyone else (and Majlis without the external id). Confirm, then Activate: your agents may name its tools from now on." },
      { id: "give", title: "Give it to your agents",
        text: "In an agent's definition, tick the connection's tools; a version that adds reach is activated by a second admin. Shared with another workspace, your agent reads it for them; their agents never reach it directly." },
    ],
    see: [
      "The connection Ready, its tools listed, and on its Activity tab every call: which agent read what, for which workspace.",
    ],
    refused: [
      { who: ["member", "noWorkspace", "viewer"], what: "There is no Add a connection for you, and another workspace's connection isn't listed for you at all.",
        then: "Ask your workspace's admins." },
      { who: ["workspaceAdmin"], what: "Your workspace's agents can't name another workspace's connection: only that workspace's own agents read it, for whoever they work for.",
        then: "Ask that workspace to share an agent with yours." },
    ],
    asOf: "2026-10-20",
  },
  {
    id: "lessons",
    title: "Teach an agent from a correction",
    summary: "When an agent's answer misses how your team works, send a correction; your workspace's reviewers turn it into a lesson the agent is given next time, and can retire it.",
    forWhom: "Anyone asking an agent sends corrections; the agent's workspace's admins review them into lessons.",
    screens: ["ask", "learning"],
    before: [
      "Lessons guide what an agent says; they never change its tools or permissions.",
    ],
    steps: [
      { id: "correct", title: "Suggest a correction on the answer", to: "/ask",
        text: "Under an answer in Ask Majlis, press Suggest a correction and say what it should have done (“report times in UTC”). The button names who reviews it; you get a receipt." },
      { id: "review", title: "Reviewers write and approve the lesson", shot: "learning-review",
        text: "The agent's workspace's admins open their workspace's Learning, Review: Draft a lesson from up to ten corrections for one agent, or Write a lesson, then approve it. An approval made on a page someone else changed meanwhile is refused; read it again." },
      { id: "again", title: "Ask again",
        text: "Ask this question again: the new run is given the lesson, and the answer shows which lessons it used." },
      { id: "retire", title: "Retire a lesson",
        text: "Retiring stops future runs from getting it; answers already given keep their record." },
    ],
    see: [
      "“Used lesson L-…” on the next answer, and the lesson on the workspace's Learning page with who approved it.",
    ],
    refused: [
      { who: ["member", "noWorkspace", "viewer"], what: "Another workspace's review queue and lessons aren't yours: the page and the API refuse them.",
        then: "Send your correction; that workspace's admins decide." },
    ],
    asOf: "2026-10-20",
  },
];

export function topicById(id: string): GuideTopic | undefined {
  return TOPICS.find((t) => t.id === id);
}

/** The topics that walk through a screen, for "About this screen". */
export function topicsFor(screen: string): GuideTopic[] {
  const base = screen.split(":")[0];
  return TOPICS.filter((t) => t.screens.includes(screen) || t.screens.includes(base));
}
